import './PolicyStyles.css';
import { exportToWord } from '../utils/exportToWord';

const InvoicingPolicy = ({
  clientName,
  companyNumber,
  clientAddress,
  effectiveDate,
  hrContact,
  transportManagerContact,
}) => {
  const handleExportToWord = () => {
    const policyContent = document.getElementById('policy-content');
    exportToWord(policyContent.innerHTML, "Invoicing_Policy.docx");
  };

  return (
    <div className="policy-container" id="policy-content">
      <h3 className="policy-title">Invoicing Policy</h3>

      <p className="policy-details"><strong>Company Name:</strong> {clientName || "[Insert Company Name]"}</p>
      <p className="policy-details"><strong>Company Number:</strong> {companyNumber || "[Insert Company Number]"}</p>
      <p className="policy-details"><strong>Company Address:</strong> {clientAddress || "[Insert Company Address]"}</p>
      <p className="policy-details"><strong>Policy Effective Date:</strong> {effectiveDate || "[Insert Effective Date]"}</p>

      <h4 className="section-title">1. Purpose</h4>
      <p className="section-content">
        {clientName || "The Company"} sets out this Invoicing Policy to ensure invoices are created, issued and managed consistently and accurately for all
        transport operations (HGV/PSV/Van). Clear procedures support timely payment, professional relationships with customers and robust financial control.
      </p>

      <h4 className="section-title">2. Scope</h4>
      <p className="section-content">
        This policy applies to all employees involved in job booking, service delivery, administration and accounts. It covers creation, approval, submission,
        query handling, credit notes, payment terms and late fees for all transport services and related charges.
      </p>

      <h4 className="section-title">3. Invoice Creation</h4>
      <p className="section-content">
        Invoices must be raised from accurate source data (job sheets, PODs, tachograph/telematics where relevant) and include: unique invoice number and date;
        customer details; PO/reference; service dates; locations; vehicle registration (if required); description of service; rates and quantities; surcharges
        (e.g., waiting time, tolls, LEZ/ULEZ/CAZ); VAT where applicable; and total amounts. All invoices must reflect the agreed rate card or contract.
      </p>

      <h4 className="section-title">4. Sending & Submission</h4>
      <p className="section-content">
        Invoices are normally issued weekly unless the contract specifies otherwise. Submit to the customer’s nominated email/portal with any required references
        and attachments (e.g., signed PODs). Where customer portals are used, upload within the stated timeframes and retain submission confirmations.
      </p>

      <h4 className="section-title">5. Payment Terms & Methods</h4>
      <p className="section-content">
        Standard terms are <strong>Net 30 days</strong> from receipt of a valid invoice unless otherwise agreed in writing. Preferred payment method is <strong>BACS</strong> to the
        account shown on the invoice. Remittance advice should be sent to the accounts email address. Foreign currency billing requires prior written agreement.
      </p>

      <h4 className="section-title">6. Late Fees & Overdue Accounts</h4>
      <p className="section-content">
        The Company may apply statutory interest and fixed‑sum compensation in accordance with the Late Payment of Commercial Debts (Interest) Act 1998 (as amended)
        on overdue invoices. Services may be suspended for persistent non‑payment following reasonable notice. Part‑paid and disputed items will be managed in line
        with this policy.
      </p>

      <h4 className="section-title">7. Queries, Disputes & Credit Notes</h4>
      <p className="section-content">
        Customers should raise invoice queries promptly (normally within 7 working days of receipt). The Accounts team will investigate, request evidence where
        necessary, and respond in a timely manner. If an error is confirmed, a credit note will be issued and reconciled against the relevant invoice. Undisputed
        amounts must be paid on time while queries are under review.
      </p>

      <h4 className="section-title">8. Compliance & Data Protection</h4>
      <p className="section-content">
        Invoicing must comply with applicable tax/VAT requirements and contractual terms. Personal data (e.g., contact names, vehicle or driver identifiers where
        present in attachments) must be handled in accordance with UK GDPR and the Company’s Data Protection Policy.
      </p>

      <h4 className="section-title">9. Record Keeping & Retention</h4>
      <p className="section-content">
        All invoices, credit notes, remittances and supporting evidence (PODs, job sheets, correspondence) must be stored securely and retained in line with the
        Company’s retention schedule and legal requirements. Electronic records are acceptable where legible and accessible for audit.
      </p>

      <h4 className="section-title">10. Roles & Responsibilities</h4>
      <p className="section-content">
        Operations teams must provide accurate job data and timely PODs. Accounts are responsible for invoice accuracy, issue and reconciliation. Managers are
        responsible for approving rates and surcharges and resolving escalated disputes. The Transport Manager may be consulted where operational details
        (e.g., waiting time or route constraints) affect charges.
      </p>

      <h4 className="section-title">Contact Information</h4>
      <ul>
        <li><strong>Human Resources:</strong> {hrContact || "[Insert Contact Number]"}</li>
        <li><strong>Transport Manager:</strong> {transportManagerContact || "[Insert Contact Number]"}</li>
      </ul>

      <h4 className="section-title">Employee Acknowledgment and Signature</h4>
      <p>I confirm that I have read and understood the Invoicing Policy and agree to its terms.</p>
      <p>Employee Name: _________________________</p>
      <p>Employee Signature: _________________________</p>
      <p>Date: _________________________</p>

      <h4 className="section-title">Management Approval and Signature</h4>
      <p>This policy has been approved by senior management and is now in effect.</p>
      <p>Manager/Officer Name: _________________________</p>
      <p>Title: _________________________</p>
      <p>Signature: _________________________</p>
      <p>Date: _________________________</p>

      <div className="mt-4 text-center">
        <button
          onClick={handleExportToWord}
          className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition"
        >
          Export to Word
        </button>
      </div>
    </div>
  );
};

export default InvoicingPolicy;